import { useEffect, useMemo, useState } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import { FLAGS } from '../platform/config/flags';
import { logger } from '../shared/utils/logger';
import { journeyLoaders, DEFAULT_JOURNEY_ID } from './registry';

/**
 * Resolve a `journeyId` to its manifest. Every id resolves asynchronously, and an ops toggle
 * can revoke (never grant) a declared block. See .claude/context/journey-resolution.md.
 *
 * @param {string} [journeyId] a journeyId already normalized by `resolveJourneyId`
 * @returns {{ journey: object, isResolving: boolean }} `journey` is the manifest itself when no
 *   override applies, so its referential identity is preserved.
 */

// Plain literal, not an import — see .claude/context/journey-resolution.md.
const EMPTY_JOURNEY = { id: null, flows: {}, config: {}, content: null };

export const useResolvedJourney = (journeyId) => {
  // Unregistered id falls back to the default's OWN loader, never a static value.
  const loader = journeyLoaders[journeyId] ?? journeyLoaders[DEFAULT_JOURNEY_ID];
  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loader()
      .then((mod) => { if (!cancelled) setResolved(mod.default ?? EMPTY_JOURNEY); })
      .catch(() => { if (!cancelled) setResolved(EMPTY_JOURNEY); });
    return () => { cancelled = true; };
  }, [journeyId, loader]);

  const declared = resolved ?? EMPTY_JOURNEY;

  // One line per ops toggle: read the raw flag, named positively after the flag itself.
  const isLiveAgentEnabled = useConfigService(FLAGS.liveAgentEnabled);

  const journey = useMemo(() => {
    if (isLiveAgentEnabled !== false) return declared;
    logger.log(
      `[journeys] "${declared.id}" declared liveAgent=${String(declared.config?.liveAgent)}; `
      + `revoked by ${FLAGS.liveAgentEnabled}=false`
    );
    return { ...declared, config: { ...declared.config, liveAgent: false } };
  }, [declared, isLiveAgentEnabled]);

  return { journey, isResolving: resolved === null };
};
